import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { http } from "@/lib/api";

const axis = { fontSize: 11, fill: "var(--ink-3)" };
const tip = { background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12 };
const label = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" });

const Stat = ({ k, n, testid }) => (
  <div className="card p-4"><p className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--ink-3)" }}>{k}</p><p data-testid={testid} className="font-display text-3xl font-bold mt-1">{n}</p></div>
);

const Chart = ({ title, children, testid }) => (
  <div className="card p-4" data-testid={testid}>
    <p className="text-sm font-semibold mb-2">{title}</p>
    <div style={{ height: 200 }}><ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer></div>
  </div>
);

export const UsageStats = () => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get("/admin/usage").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === null) return <Loader2 className="mt-6 animate-spin" />;
  if (!d) return <p className="mt-6 text-sm">Couldn't load usage.</p>;
  const daily = d.daily.map((r) => ({ ...r, label: label(r.day) }));
  const t = d.totals;
  return (
    <div className="mt-6 space-y-4" data-testid="admin-usage-panel">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat k="Accounts" n={t.accounts} testid="admin-usage-accounts" />
        <Stat k="Cases" n={t.cases} testid="admin-usage-cases" />
        <Stat k="Driving Qs" n={t.driving} testid="admin-usage-driving" />
        <Stat k="Mock days" n={t.mock_days} testid="admin-usage-mocks" />
        <Stat k="Feedback" n={t.feedback} testid="admin-usage-feedback" />
      </div>
      <Chart title="Daily users, last 30 days" testid="admin-usage-daily-users">
        <BarChart data={daily} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
          <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={false} interval={4} />
          <YAxis allowDecimals={false} tick={axis} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={tip} cursor={{ fill: "var(--muted-bg)" }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="users" name="Signed in" stackId="u" fill="var(--accent)" />
          <Bar dataKey="guests" name="Guests" stackId="u" fill="var(--warn)" />
        </BarChart>
      </Chart>
      <Chart title="Activity per day, last 30 days" testid="admin-usage-activity">
        <LineChart data={daily} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
          <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={false} interval={4} />
          <YAxis allowDecimals={false} tick={axis} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={tip} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line dataKey="cases" name="Cases" stroke="var(--accent)" strokeWidth={2} dot={false} />
          <Line dataKey="driving" name="Driving Qs" stroke="var(--warn)" strokeWidth={2} dot={false} />
          <Line dataKey="feedback" name="Feedback" stroke="var(--ok)" strokeWidth={2} dot={false} />
        </LineChart>
      </Chart>
      <p className="text-xs" style={{ color: "var(--ink-3)" }}>Counts only. No names, emails or results. Daily users are counted from today; days are Sydney time.</p>
    </div>
  );
};
